import React, { useEffect, useState, useMemo } from "react";
import GeneralMUIDialog from "../../../components/GeneralMUIDialog/GeneralMUIDialog";
import {
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
} from "@mui/material";
import {
  edit_access_of_invitation,
  send_invitation_to_user,
  verify_invited_user_email,
} from "../../../DAL/manage-users/manage-users";
import { enqueueSnackbar } from "notistack";
import { show_proper_words } from "../../../utils/Constant";
import InfoIconWithCustomTooltip from "../../../components/InfoIconWithTooltip/InfoIconWithCustomTooltip";
import { useIds } from "../../../hook/AdminContext";
import { email, email_icon } from "../../../assets";

export default function AddorEditInternalExternalUsers({
  openAddEditDialog,
  setOpenAddEditDialog,
  type = "external",
  handleGetSentInvitation = () => {},
  targetInvitation = null,
}) {
  const { userInfo, fetctUserDetail } = useIds();
  // Initial form state
  const [inputs, setInputs] = useState({
    email: "",
    access: {
      job: { add: false, update: false, delete: false, global_access: false },
      feed: { add: false, update: false, delete: false, global_access: false },
      manage_profile: {
        company_profile: false,
        // manage_media: false,
        // manage_achievements: false,
        manage_users: false,
      },
    },
  });

  const [formType, setFormType] = useState("add");

  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
    setEmailErrorMessage("");
  };

  // Handle checkbox toggle
  const handleAccessChange = (section, key) => {
    setInputs((prev) => ({
      ...prev,
      access: {
        ...prev.access,
        [section]: {
          ...prev.access[section],
          [key]: !prev.access[section][key],
        },
      },
    }));
  };

  // Handle select all / unselect all
  const toggleAll = (selectAll) => {
    const updatedAccess = Object.fromEntries(
      Object.entries(inputs.access).map(([section, permissions]) => [
        section,
        Object.fromEntries(Object.keys(permissions).map((k) => [k, selectAll])),
      ])
    );

    setInputs((prev) => ({
      ...prev,
      access: updatedAccess,
    }));
  };

  const allSelected = Object.values(inputs.access).every((section) =>
    Object.values(section).every(Boolean)
  );

  // Handle form submit
  const handleSubmit = async () => {
    if (Boolean(emailErrorMessage)) {
      console.log(
        emailErrorMessage,
        "emailErrorMessageemailErrorMessageemailErrorMessage"
      );
      enqueueSnackbar(emailErrorMessage, { variant: "error" });
      return;
    }
    const hasAtLeastOneAccess = Object.values(inputs.access).some((section) =>
      Object.values(section).some(Boolean)
    );

    if (!hasAtLeastOneAccess) {
      enqueueSnackbar(
        "You must assign at least one access permission before proceeding.",
        {
          variant: "error",
        }
      );
      return;
    }
    setIsLoading(true);
    let postData = {
      access: {
        job: {
          add: inputs.access.job.add,
          update: inputs.access.job.update,
          delete: inputs.access.job.delete,
          global_access: inputs.access.job.global_access,
        },
        feed: {
          add: inputs.access.feed.add,
          update: inputs.access.feed.update,
          delete: inputs.access.feed.delete,
          global_access: inputs.access.feed.global_access,
        },
        manage_profile: {
          company_profile: inputs.access.manage_profile.company_profile,
          // manage_media: inputs.access.manage_profile.manage_media,
          // manage_achievements: inputs.access.manage_profile.manage_achievements,
          manage_user: inputs.access.manage_profile.manage_users,
        },
      },
    };

    if (formType == "add") {
      postData = {
        ...postData,
        user_type: type,
        email: inputs.email,
      };
    }

    const resp =
      formType == "add"
        ? await send_invitation_to_user(postData)
        : await edit_access_of_invitation(targetInvitation._id, postData);

    if (resp.code == 200) {
      handleGetSentInvitation();
      enqueueSnackbar(resp.message, { variant: "success" });
      setOpenAddEditDialog(false);

      if (formType == "edit") {
        if (targetInvitation.email == userInfo.email) {
          fetctUserDetail();
        }
      }
    } else {
      enqueueSnackbar(resp.message, { variant: "error" });
      setIsLoading(false);
    }
  };

  // Validate email with debounce
  const handleValidateUser = async () => {
    const postData = {
      email: inputs.email,
      user_type: type,
    };

    const resp = await verify_invited_user_email(postData);
    if (resp.code !== 200) {
      setEmailErrorMessage(resp.message);
    }
  };

  useEffect(() => {
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formType == "add" && inputs.email && isValidEmail.test(inputs.email)) {
      const timer = setTimeout(() => {
        handleValidateUser();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [inputs.email]);

  useEffect(() => {
    if (openAddEditDialog && targetInvitation) {
      setInputs((prev) => ({
        ...prev,
        email: targetInvitation.email,
        access: {
          job: {
            add: targetInvitation.access.job.add,
            update: targetInvitation.access.job.update,
            delete: targetInvitation.access.job.delete,
            global_access: targetInvitation.access.job.global_access || false,
          },
          feed: {
            add: targetInvitation.access.feed.add,
            update: targetInvitation.access.feed.update,
            delete: targetInvitation.access.feed.delete,
            global_access: targetInvitation.access.feed.global_access || false,
          },
          manage_profile: {
            company_profile:
              targetInvitation.access.manage_profile.company_profile,
            // manage_media: targetInvitation.access.manage_profile.manage_media,
            // manage_achievements:
            // targetInvitation.access.manage_profile.manage_achievements,
            manage_users: targetInvitation.access.manage_profile.manage_user,
          },
        },
      }));
      setFormType("edit");
    } else {
      setFormType("add");
    }
  }, [openAddEditDialog]);

  // Render access checkboxes
  const renderAccessGroup = (
    sectionLabel,
    sectionKey,
    tooltipContent = null
  ) => {
    const permissions = inputs.access[sectionKey];

    return (
      <div
        key={sectionKey}
        className="access-box"
        style={{ marginTop: "1rem" }}
      >
        <span>
          {sectionLabel}
          {/* {tooltipContent && (
            <InfoIconWithCustomTooltip toolTipInfo={tooltipContent} />
          )} */}
        </span>
        {/* <FormGroup row>
          {Object.entries(permissions).map(([key, value]) => (
            <FormControlLabel
              key={key}
              control={
                <Checkbox
                  size="medium"
                  checked={value}
                  className="mt-2 user-checkbox"
                  onChange={() => handleAccessChange(sectionKey, key)}
                />
              }
              label={
                <span style={{ fontSize: 14 }}>
                  {key
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                </span>
              }
            />
          ))}
        </FormGroup> */}
        <div className="d-flex flex-wrap">
          {Object.entries(permissions).map(([key, value]) => (
            <div className="form-check me-4 mt-2 gap-2" key={key}>
              <input
                className="form-check-input user-checkbox"
                type="checkbox"
                id={`${sectionKey}-${key}`}
                checked={value}
                onChange={() => handleAccessChange(sectionKey, key)}
              />
              <label
                className="form-check-label"
                htmlFor={`${sectionKey}-${key}`}
                style={{ fontSize: 14 }}
              >
                {key
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (l) => l.toUpperCase())}
              </label>
            </div>
          ))}
        </div>

        {/* <Divider sx={{ marginTop: 1 }} /> */}
      </div>
    );
  };

  // Form Component (memoized to preserve focus)
  const AddEditUserForm = useMemo(
    () => (
      <>
        {/* <div className="custom-input-design">
          <label>Email*</label>
          <div className="position-relative">
            <input
              fullWidth
              name="email"
              disabled={formType == "edit"}
              type="email"
              label="User Email"
              className="mb-4"
              value={inputs.email}
              onChange={handleChange}
              // required
              error={Boolean(emailErrorMessage)}
              helperText={emailErrorMessage}
            />
            <img src={email_icon} className="input-start-icon" alt="" />
          </div>
        </div> */}
        <div className="custom-input-design mb-4">
          <label>Email*</label>

          <div className="position-relative">
            <input
              className="ps-5"
              type="email"
              name="email"
              placeholder="User Email"
              onChange={handleChange}
              value={inputs.email}
            />

            <img src={email} className="input-start-icon" alt="" />
          </div>
        </div>
        {/* Access Permissions */}
        <div className="">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0 me-2">Access Permissions</h6>
            <button
              className="btn-outline mt-2 mt-md-0"
              type="button"
              onClick={() => toggleAll(!allSelected)}
            >
              {allSelected ? "Unselect All" : "Select All"}
            </button>
          </div>

          {renderAccessGroup(
            "Job Access",
            "job",
            "Allows the user to add, update, and delete job posts. Enabling Global Access also allows the user to view, edit, and delete jobs created by other users"
          )}
          {renderAccessGroup(
            "Feed Access",
            "feed",
            "Allows the user to add, update, and delete feed posts. Enabling Global Access also allows the user to edit, and delete feed posts created by other users. These permissions automatically extend to all actions on comments as well."
          )}
          {renderAccessGroup(
            "Manage Profile Access",
            "manage_profile",
            "The user is granted high-level permissions to change the main company information and/or manage (invite/remove) other users. If company access is granted, the user can also manage insights, coaches, and company profiles across different sources such as Mindset Source, Training Source, and HR."
          )}
        </div>
      </>
    ),
    [inputs, emailErrorMessage]
  );

  return (
    <>
      {openAddEditDialog && (
        <GeneralMUIDialog
          open={openAddEditDialog}
          setOpen={setOpenAddEditDialog}
          title={
            formType == "add"
              ? `Invite ${show_proper_words(type)} User`
              : `Update Access`
          }
          actionText={formType == "add" ? `Invite` : `Update Access`}
          actionSubmittingText={
            formType == "add" ? `Sending...` : `Updating...`
          }
          loading={isLoading}
          content={AddEditUserForm}
          onSubmit={handleSubmit}
          disableAction={Boolean(emailErrorMessage)}
          disableReason={emailErrorMessage}
        />
      )}
    </>
  );
}
